/**
 * TimeDisplay
 *
 * Self-contained time display component for video playback.
 * Shows current time and total duration in MM:SS or HH:MM:SS format.
 *
 * Performance:
 * - Queries video element directly from registry
 * - Updates only once per second (no need for higher frequency)
 * - Uses formatDuration from MediaUtils for consistent formatting
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { videoElementRegistry } from '@/presentation/registries/videoElementRegistry';
import { MediaUtils } from '@/domain/utils/mediaUtils';

export interface VideoPlayblackCurrentProgressProps {
  /** Video key to identify which video to track */
  videoKey: string;

  /** Total duration in seconds */
  duration: number;
}

export const VideoPlayblackCurrentProgress: React.FC<VideoPlayblackCurrentProgressProps> = ({ videoKey, duration }) => {
  const { theme } = useTheme();
  const [currentTime, setCurrentTime] = useState(0);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    // Get video element from registry once and store in ref
    const videoElement = videoElementRegistry.get(videoKey);
    if (!videoElement) return;

    videoElementRef.current = videoElement;

    // Set initial time immediately (important for paused videos and fullscreen)
    setCurrentTime(videoElement.currentTime);

    let animationFrameId: number;
    let lastUpdateTime = 0;
    const UPDATE_INTERVAL = 1000; // Update once per second for time display

    const updateTime = (timestamp: number) => {
      const video = videoElementRef.current;

      if (video && !video.paused && timestamp - lastUpdateTime >= UPDATE_INTERVAL) {
        setCurrentTime(video.currentTime);
        lastUpdateTime = timestamp;
      }

      animationFrameId = requestAnimationFrame(updateTime);
    };

    animationFrameId = requestAnimationFrame(updateTime);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [videoKey]);

  return (
    <div className="text-sm font-medium" style={{ color: theme.white }}>
      {MediaUtils.formatVideoProgress(currentTime, duration)}
    </div>
  );
};
